import { useMutation } from '@tanstack/react-query';
import { useWatch } from 'react-hook-form';
import { z } from 'zod';

import type { SaveOptions } from 'payments/types/form';
import { useSlug } from 'reservations/account/providers/ConfigProvider';
import { BENTOBOX_SAVE_CARD_ON_FILE_ENDPOINT } from 'reservations/base/constants/endpoints';
import { buildBentoboxApiEndpoint } from 'reservations/base/helpers/buildBentoboxApiEndpoint';
import { useErrorSnackbar } from 'reservations/base/hooks/useSnackbar';
import { useLoadingSpinner } from 'reservations/base/providers/LoadingSpinnerProvider';
import { useCustomRouter } from 'reservations/base/providers/RouterProvider';
import { useDinerAccount } from 'reservations/dinerAccount/hooks/useDinerAccount';
import { type RequestError, restClient } from 'utility/restClient';

const SaveCardOnFile = z.object({
  booking_id: z.string(),
  should_redirect_to_confirmation: z.boolean()
});
export type SaveCardOnFile = z.infer<typeof SaveCardOnFile>;

export function useSaveCardOnFileMutation(
  handleSuccess: () => void,
  handle409Error?: () => void
) {
  const slug = useSlug();
  const { handleNavigation } = useCustomRouter();
  const { dinerEmail, setBeginAuthParams } = useDinerAccount();
  const enqueueErrorSnackbar = useErrorSnackbar();
  const { handleHasLoaded } = useLoadingSpinner();
  const email = useWatch({
    name: 'email'
  });

  return useMutation<SaveCardOnFile, RequestError, SaveOptions>({
    mutationFn: async (formData: SaveOptions) => {
      const cardOnFileEndpoint = buildBentoboxApiEndpoint(
        BENTOBOX_SAVE_CARD_ON_FILE_ENDPOINT
      );
      const response = await restClient.post(cardOnFileEndpoint, {
        data: formData
      });

      // NOTE: We don't want to throw when we parse the response
      // because we don't want to throw an error if we've actually saved
      // the card on file successfully.
      // We only want to throw if for some reason we cannot direct to the confirmation page.
      const data = response as SaveCardOnFile;
      const shouldDirectToConfirmation =
        data?.should_redirect_to_confirmation ?? false;

      if (!shouldDirectToConfirmation) {
        throw new Error("Card on file wasn't saved successfully");
      }

      return data;
    },
    onSuccess: data => {
      const { booking_id, should_redirect_to_confirmation } = data;

      if (should_redirect_to_confirmation) {
        handleNavigation(`/locations/${slug}/booking/${booking_id}`);
      }

      handleSuccess();
    },
    onError: (error, _) => {
      handleHasLoaded();

      let message =
        'Something went wrong saving your card on file. Please try again.';

      if (error.code === 409) {
        message =
          'Please enter the verification code to complete your reservation.';

        // Sometimes autofill doesn't call handleOnBlur. This triggers the OTP component in those instances
        if (dinerEmail) {
          setBeginAuthParams(dinerEmail);
        } else if (email) {
          setBeginAuthParams(email);
        }

        handle409Error?.();
      }

      enqueueErrorSnackbar('SAVE_CARD_ON_FILE_ERROR', message);
    }
  });
}
